import { Component, ElementRef, OnInit, QueryList, signal, ViewChildren, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { CorsService } from '../../../service/cors.service';
import { CorsConfig as CorsConfigModel } from '../../models/cors.model';

import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { InputText } from 'primeng/inputtext';

import { PageLayoutComponent } from '../../shared/page-layout/page-layout';
import { Button, ButtonDirective } from 'primeng/button';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { StateContainerComponent, ViewState } from '../../shared/state-container/state-container.component';

type CorsConfigUI = CorsConfigModel & {
  isNew?: boolean;
};

@Component({
  selector: 'app-cors-config',
  standalone: true,
  templateUrl: './cors-config.html',
  styleUrl: './cors-config.css',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    InputText,
    ToastModule,
    PageLayoutComponent,
    ButtonDirective,
    ToggleSwitch,
    Button,
    ConfirmDialog,
    StateContainerComponent
  ],
  providers: [MessageService, ConfirmationService, CorsService]
})
export class CorsConfig implements OnInit {
  corsConfigs = signal<CorsConfigUI[]>([]);
  loading = signal(false);
  error = signal<any>(null);
  editingId = signal<number | null>(null);
  @ViewChildren('originInput') inputs!: QueryList<ElementRef>;

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (this.corsConfigs().length === 0) return 'empty';
    return 'content';
  });

  readonly emptyStateMessage = computed(() => {
    return 'Add your first CORS configuration to manage cross-origin requests.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load CORS configurations. Please try again.';
  });

  constructor(
    private corsService: CorsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadCorsConfigs();
  }

  // ✅ LOAD DATA
  loadCorsConfigs(): void {
    this.loading.set(true);
    this.error.set(null);

    this.corsService.getCorsConfigs().subscribe({
      next: (res) => {
        this.corsConfigs.set(Array.isArray(res) ? res : []);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load CORS configs',
          life: 3000
        });
      }
    });
  }

  // Retry method for error state
  retryLoad(): void {
    this.loadCorsConfigs();
  }

  // ✅ CHECK UNSAVED ROW
  hasUnsavedRow(): boolean {
    return this.corsConfigs().some(r => r.isNew);
  }

  // ✅ ADD NEW ROW
  addNewRow(): void {
    const newRow: CorsConfigUI = {
      originPattern: '',
      enabled: true,
      isNew: true
    } as CorsConfigUI;

    this.corsConfigs.update(list => [newRow, ...list]);
    this.editingId.set(-1); // just marker
  }

  // ✅ START EDIT
  startEdit(row: CorsConfigUI): void {
    this.editingId.set(row.id);
    setTimeout(() => {
      const input = this.inputs.first;
      input?.nativeElement.focus();
    });
  }

  // ✅ CANCEL EDIT
  cancelEdit(): void {
    this.corsConfigs.update(list => list.filter(r => !r.isNew));
    this.editingId.set(null);
  }

  // ✅ TOGGLE ENABLE/DISABLE
  toggleStatus(row: CorsConfigUI): void {
    if (row.isNew) return;

    this.corsService.updateCors(row.id, row).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Updated',
          detail: 'Status updated successfully',
          life: 2000
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update status',
          life: 2000
        });

        this.loadCorsConfigs();
      }
    });
  }

  // ✅ SAVE (CREATE / UPDATE)
  updateRow(row: CorsConfigUI): void {
    if (!row.originPattern?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Origin pattern is required',
        life: 2000
      });
      return;
    }

    const request$ = row.isNew
      ? this.corsService.createCors(row)
      : this.corsService.updateCors(row.id, row);

    request$.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: row.isNew
            ? 'CORS config created successfully'
            : 'CORS config updated successfully',
          life: 2000
        });

        this.editingId.set(null);
        this.loadCorsConfigs();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Operation failed',
          life: 2000
        });
      }
    });
  }

  deleteRow(row: CorsConfigUI): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this CORS config?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.corsService.deleteCors(row.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Deleted',
              detail: 'CORS config deleted successfully',
              life: 2000
            });

            this.loadCorsConfigs();
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete CORS config',
              life: 2000
            });
          }
        });
      }
    });
  }
}